// Fare Validator Type Definitions

export interface LocationCoords {
  lat: number;
  lng: number;
}

export type InputMethod = 'pin' | 'manual';

export type RoutePreference = 'shortest' | 'fastest' | 'balanced';

export interface FareRequest {
  start: LocationCoords;
  end?: LocationCoords;
  distanceKm?: number;
  inputMethod: InputMethod;
  date?: string;
  userPreference?: RoutePreference;
}

export type TransportMode = 'tuk' | 'taxi';

export type WeatherSeverity = 'none' | 'moderate' | 'high';

export interface WeatherImpact {
  severity: WeatherSeverity;
  note: string | null;
  source: string;
  appliedMultiplier: number;
}

export interface FareData {
  mode: TransportMode;
  min: number;
  max: number;
  base: number;
  ceiling: number;
}

export interface ModeFares {
  min: number;
  max: number;
  base: number;
}

export interface BothModes {
  tuk: ModeFares;
  taxi: ModeFares;
}

export interface ExchangeRates {
  USD: number;
  GBP: number;
  EUR: number;
  CNY: number;
  RUB: number;
}

export interface RouteFare {
  mode?: TransportMode;
  base?: number;
  min?: number;
  max?: number;
  ceiling?: number;
  modes?: BothModes;
}

export interface RouteDetails {
  distanceKm: number;
  durationMins: number;
  summary: string;
  reasoning?: string;
  polyline: string | null;
  fare?: RouteFare;
}

export interface AlternativeRoute extends RouteDetails {
  savings: {
    distanceKm: number;
    timeMins: number;
  };
}

export interface RouteInfo {
  recommended: RouteDetails;
  alternatives: AlternativeRoute[];
  totalRoutesFound: number;
}

export interface FareResponse {
  // Metadata
  timestamp: string;
  origin: LocationCoords | null;
  destination: LocationCoords | null;
  distanceKm: number;
  inputMethod: string;

  // Context & Conditions
  context: string[];
  hotspot: string | null;
  weather: WeatherImpact;
  exchangeRates: ExchangeRates;

  // Fare Calculation (one of these scenarios)
  fare?: FareData;  // Single mode recommendation (<15km or >50km)
  modes?: BothModes;  // Both modes (15-50km)
  premium?: number;  // taxi.base - tuk.base (when both modes available)
  offerTuk?: boolean;
  offerTaxi?: boolean;

  // Route Information (if pin-to-pin mode)
  route?: RouteInfo;

  // Optional Fields
  negotiationTip?: string;
}

export interface FareErrorResponse {
  error: string;
  message: string;
  serviceArea?: string;
  maxDistance?: string;
  requestedDistance?: string;
}

// Quick route preset interface
export interface RoutePreset {
  id: string;
  name: string;
  start: LocationCoords;
  end: LocationCoords;
  distanceKm: number;
  description?: string;
}

// Map marker types
export type MarkerType = 'pickup' | 'destination';

export interface FareMarker {
  id: string;
  position: LocationCoords;
  type: MarkerType;
  title: string;
}

// Currency type
export type Currency = 'LKR' | 'USD' | 'GBP' | 'EUR' | 'CNY' | 'RUB';

// Selection mode for map interaction
export type SelectionMode = 'none' | 'pickup' | 'destination';

// UI State types
export interface FareCalculationState {
  loading: boolean;
  error: string | null;
  data: FareResponse | null;
}

export interface LocationInputState {
  mode: 'autocomplete' | 'pin' | 'manual';
  pickupLocation: LocationCoords | null;
  pickupAddress: string;
  destinationLocation: LocationCoords | null;
  destinationAddress: string;
  manualDistance: number | null;
}

export interface MapState {
  selectedRouteIndex: number;
  zoom: number;
  center: LocationCoords;
}

// Type guards
export function isSingleModeFare(response: FareResponse): response is FareResponse & { fare: FareData } {
  return response.fare !== undefined && response.fare !== null;
}

export function isBothModesFare(response: FareResponse): response is FareResponse & { modes: BothModes } {
  return response.modes !== undefined && response.modes !== null;
}

export function hasRouteInfo(response: FareResponse): response is FareResponse & { route: RouteInfo } {
  return response.route !== undefined && response.route !== null;
}

